<template>
  <div :class="cn('flex flex-col gap-6 rounded-2xl border p-5', props.class)">
    <MessageSquareMore class="size-8 -scale-x-100" />

    <div class="gap-inherit flex flex-col" v-auto-animate="autoAnimateFade">
      <UiSkeleton
        v-if="isUserMeetingsLoading"
        loading
        :repeat="5"
        :classes="{ line: 'first:mb-1 first:h-9 first:w-1/2', container: 'pt-0.5' }"
      />

      <template v-else-if="!hasActiveMeetings">
        <div class="flex flex-col gap-2">
          <span class="text-xl font-semibold">{{ tsafe("beforeMeeting.title") }}</span>
          <span class="text-sm text-neutral-700">{{ tsafe("beforeMeeting.description") }}</span>
        </div>

        <UiButton class="w-fit" :to="calendlyUrl" @click="pollForNewMeetings">{{
          tsafe("beforeMeeting.cta")
        }}</UiButton>
      </template>

      <template v-else>
        <div class="flex flex-col gap-2">
          <span class="text-xl font-semibold">{{ tsafe("afterMeeting.title") }}</span>
          <span class="text-sm text-neutral-700">{{ tsafe("afterMeeting.description") }}</span>
        </div>

        <ul class="flex list-inside list-disc flex-col gap-2">
          <li class="text-sm" v-for="bullet in tmsafe('afterMeeting.bullets')" :key="bullet">{{ bullet }}</li>
        </ul>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MessageSquareMore } from "lucide-vue-next"

const props = defineProps<{
  class?: ClassValue
}>()

const { tsafe, tmsafe } = useI18nUtils()
const { calendlyUrl } = useCalendlyUrl()
const {
  data: userMeetings,
  isLoading: isUserMeetingsLoading,
  refetch: refetchUserMeetings,
} = useUserMeetings()

const hasActiveMeetings = computed(() => {
  return userMeetings.value?.data.some((meeting) => meeting.status === "active")
})

// Call every 30 seconds for 30 minutes
const { start: pollForNewMeetings } = useDoWhile({
  interval: 30 * 1000,
  counterTillTimeout: 60,
  callback: async (stop: VoidFunction) => {
    await refetchUserMeetings()

    if (hasActiveMeetings.value) {
      stop()
    }
  },
  onTimeout: () => {},
})
</script>

<i18n lang="json">
{
  "he": {
    "beforeMeeting": {
      "title": "יש דברים שחשוב לדייק ביחד",
      "description": "ה-AI שלנו עשה את רוב העבודה – אבל יש עוד כמה נקודות שחשוב לעבור עליהן יחד. שיחה קצרה עם אחד המומחים שלנו, תעזור לנו לוודא שהתובנות באמת מדויקות, ומותאמות בדיוק בשבילך.",
      "cta": "אני רוצה לקבוע שיחה"
    },
    "afterMeeting": {
      "title": "מתכוננים לפגישה?",
      "description": "כדי שנוכל לבדוק את כל מה שצריך ולעזור לך בצורה מדויקת, כדאי להגיע עם:",
      "bullets": [
        "תלוש שכר עדכני - כדי לוודא את השכר ברוטו וההפקדות לפנסיה",
        "תעודת זהות + ספח",
        "הסכם העסקה - כדי לבדוק אם כולל סעיף 14"
      ]
    }
  },
  "en": {}
}
</i18n>
